import { useEffect, useState } from "react";
import { Copy, ExternalLink, Loader2, RotateCcw, Search } from "lucide-react";
import bizApi from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { Badge, btnGhost, btnSecondary, cardCls, formatChf, inputCls } from "./ui";
import { OPTION_LABELS } from "./MissionBuilder";

const FILTERS = ["", "confirmed", "in_progress", "completed", "cancelled"];
const TYPE_FILTERS = ["", "transfer", "airport", "hourly", "multi_stop", "luggage", "delivery", "group", "event", "shuttle", "recurring", "custom"];

export default function Missions({ prefill, onClearPrefill, onDuplicate, onNewReturn }) {
  const { t } = useBiz();
  const [missions, setMissions] = useState(null);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (type) params.set("type", type);
    bizApi.get(`/missions?${params}`).then((r) => setMissions(r.data)).catch(() => setMissions([]));
  };
  useEffect(() => { load(); }, [status, type]);

  useEffect(() => {
    if (prefill?.missionId) {
      bizApi.get(`/missions/${prefill.missionId}`).then((r) => setDetail(r.data)).catch(() => {});
      onClearPrefill();
    }
  }, [prefill]);

  const openDetail = (id) => bizApi.get(`/missions/${id}`).then((r) => setDetail(r.data)).catch(() => {});

  const filtered = (missions || []).filter((m) => !q ||
    `${m.number} ${m.guest_name} ${m.pickup} ${m.dropoff}`.toLowerCase().includes(q.toLowerCase()));

  if (detail) {
    return <MissionDetail mission={detail} onBack={() => { setDetail(null); load(); }}
      onDuplicate={onDuplicate} onNewReturn={onNewReturn} />;
  }

  return (
    <div data-testid="missions-list">
      <h1 className="text-2xl font-bold tracking-tight text-ink">{t("missions.title")}</h1>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate/50" strokeWidth={2} />
          <input className={`${inputCls} h-9 w-56 pl-9`} placeholder={t("hub.search_placeholder")}
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="missions-search" />
        </div>
        <select className={`${inputCls} h-9 w-44`} value={type} onChange={(e) => setType(e.target.value)} data-testid="missions-type-filter">
          {TYPE_FILTERS.map((f) => <option key={f} value={f}>{f ? t(`type.${f}`) : t("missions.all")}</option>)}
        </select>
        <div className="flex max-w-full flex-wrap gap-1.5" data-testid="missions-status-filters">
          {FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setStatus(f)} data-testid={`filter-${f || "all"}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                status === f ? "bg-miles text-white" : "text-slate hover:bg-ink/[0.04]"}`}>
              {f ? t(`status.${f}`) : t("missions.all")}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {!missions && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div>}
        {missions && filtered.length === 0 && (
          <div className={`${cardCls} p-10 text-center`} data-testid="missions-empty">
            <p className="text-sm font-semibold text-ink">{t("missions.empty")}</p>
            <p className="mt-1 text-xs text-slate">{t("missions.empty_hint")}</p>
          </div>
        )}
        {filtered.map((m) => (
          <button key={m.id} type="button" onClick={() => openDetail(m.id)} data-testid={`mission-row-${m.number}`}
            className={`${cardCls} flex w-full items-center gap-4 p-4 text-left transition-all hover:border-miles/30`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-bold text-miles">{m.number}</span>
                <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 text-[10px] font-medium text-slate">{t(`type.${m.type}`)}</span>
                {m.room_number && <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 text-[10px] font-medium text-slate">Room {m.room_number}</span>}
              </div>
              <p className="mt-1 truncate text-sm font-semibold text-ink">{m.guest_name || t(`type.${m.type}`)}</p>
              <p className="truncate text-xs text-slate">{m.pickup} → {m.dropoff || "—"}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate">{m.date} · {m.time}</p>
              <p className="mt-0.5 font-mono text-[13px] font-semibold text-ink">
                {m.amount ? formatChf(m.amount) : t("status.to_quote")}
              </p>
              <div className="mt-1"><Badge tone={m.status}>{t(`status.${m.status}`)}</Badge></div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function MissionDetail({ mission: m, onBack, onDuplicate, onNewReturn }) {
  const { t, lang } = useBiz();
  const [mission, setMission] = useState(m);
  const [custodyForm, setCustodyForm] = useState({ collected: "", delivered: "", received_by: "", photo_url: "" });
  const [copiedLink, setCopiedLink] = useState(false);
  const leg = mission.legs?.[0] || {};

  const reload = () => bizApi.get(`/missions/${mission.id}`).then((r) => setMission(r.data)).catch(() => {});

  const act = (path) => bizApi.post(`/missions/${mission.id}${path}`).then(reload).catch(() => {});

  const saveCustody = () => {
    const payload = {};
    if (custodyForm.collected !== "") payload.collected = Number(custodyForm.collected);
    if (custodyForm.delivered !== "") payload.delivered = Number(custodyForm.delivered);
    if (custodyForm.received_by) payload.received_by = custodyForm.received_by;
    if (custodyForm.photo_url) payload.photo_url = custodyForm.photo_url;
    bizApi.patch(`/missions/${mission.id}/custody`, payload).then(reload).catch(() => {});
  };

  const copyLink = (code) => navigator.clipboard?.writeText(`${window.location.origin}/suivi/${code}`);

  return (
    <div data-testid="mission-detail" className="mx-auto max-w-3xl">
      <button type="button" onClick={onBack} className={btnGhost} data-testid="detail-back">← {t("mb.back")}</button>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-xl font-bold text-ink" data-testid="detail-number">{mission.number}</h1>
        <Badge tone={mission.status} testId="detail-status">{t(`status.${mission.status}`)}</Badge>
        <span className="rounded-full bg-ink/[0.05] px-2.5 py-1 text-[11px] font-medium text-slate">{t(`type.${mission.type}`)}</span>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => onDuplicate(mission)} className={btnSecondary} data-testid="detail-duplicate">
            <Copy className="h-3.5 w-3.5" /> {t("detail.duplicate")}
          </button>
          {mission.status !== "completed" && !mission.cancelled_at && (
            <button type="button" onClick={() => act("/complete")} className={btnSecondary} data-testid="detail-complete">{t("detail.complete")}</button>
          )}
          {!mission.cancelled_at && mission.status !== "completed" && (
            <button type="button" onClick={() => act("/cancel")} data-testid="detail-cancel"
              className="inline-flex h-11 items-center rounded-xl border border-red-200 bg-white px-5 text-sm font-semibold text-red-600 hover:bg-red-50">
              {t("detail.cancel")}
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <section className={`${cardCls} p-5`}>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("missions.route")}</h2>
          {(mission.legs || []).map((l, i) => (
            <div key={i} className="mt-2">
              <p className="text-sm font-semibold text-ink">{l.pickup} → {l.dropoff || t("type.hourly")}</p>
              <p className="text-xs text-slate">{l.date} · {l.time}</p>
              {l.flight?.number && <p className="mt-1 text-xs font-medium text-miles">✈ {l.flight.number}{l.flight.terminal ? ` · T${l.flight.terminal}` : ""}</p>}
              {l.train?.number && <p className="mt-1 text-xs font-medium text-miles">◉ {l.train.number}{l.train.platform ? ` · ${t("mb.platform")} ${l.train.platform}` : ""}</p>}
              {l.private_aviation?.tail_number && <p className="mt-1 text-xs font-medium text-miles">✈ {l.private_aviation.tail_number} · {l.private_aviation.fbo}</p>}
              {(l.stops || []).map((s, j) => (
                <p key={j} className="mt-0.5 text-xs text-slate">· {t("mb.stop")} {j + 1} : {s.address}{s.time ? ` (${s.time})` : ""}</p>
              ))}
            </div>
          ))}
          <p className="mt-3 text-xs text-slate">
            {(mission.vehicles || []).map((v) => `${v.count}× ${t(`mb.vehicle.${v.category}`)}`).join(" · ")}
          </p>
          {(mission.options || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {mission.options.map((o) => (
                <span key={o} className="rounded-full bg-miles-ice px-2 py-0.5 text-[10px] font-medium text-miles">
                  {(OPTION_LABELS[lang] || OPTION_LABELS.fr)[o] || o}
                </span>
              ))}
            </div>
          )}
        </section>

        <section className={`${cardCls} p-5`}>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("detail.guests")}</h2>
          {(mission.guests || []).map((g, i) => (
            <div key={i} className="mt-2">
              <p className="text-sm font-semibold text-ink">{g.first_name} {g.last_name}</p>
              <p className="text-xs text-slate">{[g.phone, g.email].filter(Boolean).join(" · ")}</p>
            </div>
          ))}
          {mission.cargo && <p className="mt-2 text-sm text-ink">{mission.cargo.bag_count} {t("mb.luggage").toLowerCase()}</p>}
          {mission.delivery && <p className="mt-2 text-sm text-ink">{mission.delivery.description}</p>}
          <div className="mt-3 border-t border-ink/[0.06] pt-3">
            <p className="text-xs text-slate">{t("mb.payment")}</p>
            <p className="text-sm font-medium text-ink" data-testid="detail-payer">
              {t(`mb.payer.${mission.payer?.type || "partner"}`)} — {t(`mb.method.${mission.payer?.method || "business_invoice"}`)}
            </p>
            <p className="mt-1 font-mono text-sm font-bold text-ink" data-testid="detail-amount">
              {mission.pricing?.amount ? formatChf(mission.pricing.amount) : t("detail.to_quote")}
            </p>
            <div className="mt-2" data-testid="detail-payment-status">
              {mission.payment?.status === "paid" ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                  {t("pay.paid")}{mission.payment.amount ? ` — ${formatChf(mission.payment.amount)}` : ""}
                </span>
              ) : mission.payer?.method === "payment_link" ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">{t("pay.pending")}</span>
                  {mission.payment?.url && (
                    <button type="button" data-testid="detail-copy-link"
                      onClick={() => { navigator.clipboard?.writeText(mission.payment.url); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 text-[11px] font-semibold text-miles ring-1 ring-miles/25 transition-colors hover:bg-miles-ice">
                      <Copy className="h-3 w-3" /> {copiedLink ? t("pay.copied") : t("pay.copy")}
                    </button>
                  )}
                </div>
              ) : mission.payer?.method === "card" ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">{t("pay.pending")}</span>
                  {mission.payment?.url && (
                    <a href={mission.payment.url} target="_blank" rel="noreferrer" data-testid="detail-pay-now"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-miles px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90">
                      <ExternalLink className="h-3 w-3" /> {t("pay.now")}
                    </a>
                  )}
                </div>
              ) : mission.payer?.method === "cash" ? (
                <span className="rounded-full bg-cloud px-2.5 py-1 text-[11px] font-semibold text-slate">{t("pay.cash_planned")}</span>
              ) : (
                <span className="rounded-full bg-miles-ice px-2.5 py-1 text-[11px] font-semibold text-miles">{t("pay.on_invoice")}</span>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* chaîne de possession bagages */}
      {mission.type === "luggage" && mission.custody && (
        <section className={`${cardCls} mt-4 p-5`} data-testid="custody-panel">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("detail.custody")}</h2>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-slate">{t("mb.bag_count")} : <b className="text-ink">{mission.custody.expected}</b></span>
            <span className="text-slate">{t("detail.collected")} : <b className="text-ink" data-testid="custody-collected">{mission.custody.collected}</b></span>
            <span className="text-slate">{t("detail.delivered")} : <b className="text-ink" data-testid="custody-delivered">{mission.custody.delivered}</b></span>
            {mission.custody.received_by && <span className="text-slate">{t("detail.received_by")} : <b className="text-ink">{mission.custody.received_by}</b></span>}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <input type="number" min="0" className={`${inputCls} h-10`} placeholder={t("detail.collected")} value={custodyForm.collected}
              onChange={(e) => setCustodyForm({ ...custodyForm, collected: e.target.value })} data-testid="custody-input-collected" />
            <input type="number" min="0" className={`${inputCls} h-10`} placeholder={t("detail.delivered")} value={custodyForm.delivered}
              onChange={(e) => setCustodyForm({ ...custodyForm, delivered: e.target.value })} data-testid="custody-input-delivered" />
            <input className={`${inputCls} h-10`} placeholder={t("detail.received_by")} value={custodyForm.received_by}
              onChange={(e) => setCustodyForm({ ...custodyForm, received_by: e.target.value })} data-testid="custody-input-received" />
            <input className={`${inputCls} h-10`} placeholder={t("detail.photo")} value={custodyForm.photo_url}
              onChange={(e) => setCustodyForm({ ...custodyForm, photo_url: e.target.value })} data-testid="custody-input-photo" />
          </div>
          <button type="button" onClick={saveCustody} className={`${btnSecondary} mt-3`} data-testid="custody-save">{t("detail.update_custody")}</button>
        </section>
      )}

      {/* véhicules & suivi */}
      {(mission.bookings || []).length > 0 && (
        <section className={`${cardCls} mt-4 p-5`} data-testid="detail-bookings">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("detail.bookings")}</h2>
          <div className="mt-2 space-y-2">
            {mission.bookings.map((b) => (
              <div key={b.tracking_code} className="flex items-center gap-3 rounded-xl border border-ink/[0.06] p-3" data-testid={`booking-${b.tracking_code}`}>
                <span className="font-mono text-xs font-bold text-miles">{b.tracking_code}</span>
                <span className="text-xs text-slate">{t(`mb.vehicle.${b.vehicle_category}`) !== `mb.vehicle.${b.vehicle_category}` ? t(`mb.vehicle.${b.vehicle_category}`) : b.vehicle_category}</span>
                <Badge tone={b.status === "booked" ? "confirmed" : b.status}>{t(`live.st.${b.status}`) !== `live.st.${b.status}` ? t(`live.st.${b.status}`) : b.status}</Badge>
                {b.driver && <span className="text-xs text-slate">{b.driver.first_name} · {b.driver.plate}</span>}
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={() => copyLink(b.tracking_code)} className={btnGhost} data-testid={`copy-link-${b.tracking_code}`}>
                    <Copy className="h-3.5 w-3.5" /> {t("detail.tracking_link")}
                  </button>
                  <a href={`/suivi/${b.tracking_code}`} target="_blank" rel="noreferrer" className={btnGhost} data-testid={`open-tracking-${b.tracking_code}`}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* références & notes */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {Object.values(mission.references || {}).some(Boolean) && (
          <section className={`${cardCls} p-5`}>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("detail.refs")}</h2>
            {Object.entries(mission.references).filter(([, v]) => v).map(([k, v]) => (
              <p key={k} className="mt-1 text-xs text-slate">{t(`mb.${k === "po_number" ? "po" : k}`)} : <span className="font-medium text-ink">{v}</span></p>
            ))}
          </section>
        )}
        {Object.values(mission.notes || {}).some(Boolean) && (
          <section className={`${cardCls} p-5`}>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate">{t("detail.notes")}</h2>
            {Object.entries(mission.notes).filter(([, v]) => v).map(([k, v]) => (
              <p key={k} className="mt-1 text-xs text-slate">{t(`mb.${k}_note`) || k} : <span className="font-medium text-ink">{v}</span></p>
            ))}
          </section>
        )}
      </div>

      {leg.dropoff && !mission.cancelled_at && (
        <button type="button" data-testid="detail-return"
          onClick={() => onNewReturn({
            ...mission,
            legs: (mission.legs || []).map((l) => ({ ...l, pickup: l.dropoff, dropoff: l.pickup, date: "", time: "" })),
          })}
          className={`${btnSecondary} mt-4`}>
          <RotateCcw className="h-3.5 w-3.5" /> {t("detail.return")}
        </button>
      )}
    </div>
  );
}
